import type { RegistryEntry } from '../integrations.js';
import { declaredOrigin } from '../iframe/origin.js';

/** The longest `url` the registry parse admits. */
export const MODULE_URL_MAX_LENGTH = 2048;

/**
 * An exact semantic version. The hen-site registry parse re-exports this
 * pattern, and the loader checks it again so a registry that skipped the parse
 * cannot pin a moving name such as `latest`.
 */
export const EXACT_SEMVER_PATTERN =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

export type ModuleContainerKind = 'shadow' | 'light';

/** The fields a `module` registry entry adds. */
export interface ModuleEntry extends RegistryEntry {
  readonly integration: 'module';
  readonly version: string;
  /** The exact, immutable file URL. Never a template. */
  readonly url: string;
  readonly origins: readonly string[];
  readonly container?: ModuleContainerKind;
  readonly lightJustification?: string;
  readonly vettingApproval?: string;
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function pathSegments(url: URL): string[] {
  try {
    return url.pathname
      .split('/')
      .filter((segment) => segment.length > 0)
      .map((segment) => decodeURIComponent(segment));
  } catch {
    return [];
  }
}

export function containerKindOf(entry: RegistryEntry): ModuleContainerKind {
  return entry['container'] === 'light' ? 'light' : 'shadow';
}

/**
 * Why a `module` entry is refused before anything is fetched, or undefined
 * when it may load. The registry parse already enforces most of this; the
 * loader repeats it because it is the last gate before partner code runs in
 * the page.
 */
export function moduleEntryRefusal(
  entry: RegistryEntry,
  allowInsecure = false,
): string | undefined {
  if (!nonEmpty(entry['vettingApproval'])) {
    return 'The module entry has no recorded vetting approval';
  }

  const container = entry['container'];
  if (
    container !== undefined &&
    container !== 'shadow' &&
    container !== 'light'
  ) {
    return 'The module entry names an unknown container';
  }
  if (container === 'light' && !nonEmpty(entry['lightJustification'])) {
    return 'A light container needs a recorded justification';
  }

  const version = entry.version;
  if (!nonEmpty(version)) {
    return 'The module entry has no version';
  }
  if (!EXACT_SEMVER_PATTERN.test(version)) {
    return 'The module entry version is not an exact semantic version';
  }

  const raw = entry['url'];
  if (typeof raw !== 'string' || raw.length === 0) {
    return 'The module entry has no url';
  }
  if (raw.length > MODULE_URL_MAX_LENGTH) {
    return `The module url is longer than ${MODULE_URL_MAX_LENGTH} characters`;
  }
  if (/[{}]/.test(raw)) {
    return 'The module url must be literal, with no placeholders';
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return 'The module url is not an absolute URL';
  }
  const protocols = allowInsecure ? ['https:', 'http:'] : ['https:'];
  if (!protocols.includes(url.protocol)) {
    return 'The module url must be https';
  }
  if (url.username || url.password) {
    return 'The module url must not carry credentials';
  }

  const origins: unknown = entry['origins'];
  if (
    !Array.isArray(origins) ||
    !origins.every((item): item is string => typeof item === 'string')
  ) {
    return "The module entry's origins are not a list of strings";
  }
  if (
    !origins.some(
      (declared) => declaredOrigin(declared, allowInsecure) === url.origin,
    )
  ) {
    return `The module url origin ${url.origin} is not one of the entry's origins`;
  }

  if (!pathSegments(url).includes(version)) {
    return 'The module url does not carry its version as a path segment';
  }
  return undefined;
}
